import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import {
  Sparkles,
  History,
  Wallet,
  Settings,
  ArrowRight,
  Copy,
  Share2,
  Gift,
  Layers,
  LayoutGrid,
  Zap,
} from 'lucide-react';
import WebApp from '@twa-dev/sdk';
import {
  formatValidUntilShort,
  pluralCredits,
  type ContentFormatCode,
  type ProfileResponse,
  type ReferralInfoResponse,
} from '@masha/shared';
import { DraftsBlock } from '../components/DraftsBlock';
import { api, type FormatDto } from '../lib/api';
import { haptic } from '../lib/tma';
import { Card, Toast } from '../components/ui';

export function HomePage() {
  const { data } = useQuery<ProfileResponse>({ queryKey: ['profile'] });
  const user = data!.user;
  const name = user.firstName ?? user.username ?? 'друг';
  const turboUntil =
    user.turboUntil && new Date(user.turboUntil).getTime() > Date.now() ? user.turboUntil : null;

  return (
    <div className="min-h-screen p-6 max-w-lg mx-auto safe-top safe-bottom">
      <header className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold leading-tight">Привет, {name}</h1>
        <div className="flex items-center gap-2">
          {turboUntil && (
            <Link
              to="/balance"
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-brand text-brand-on text-xs font-semibold transition-opacity active:opacity-70"
            >
              <Zap size={12} />
              Турбо до {formatValidUntilShort(turboUntil)}
            </Link>
          )}
          <Link
            to="/balance"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-muted text-brand text-sm font-semibold transition-opacity active:opacity-70"
          >
            <Wallet size={14} />
            {user.balanceCredits}
          </Link>
        </div>
      </header>

      <SectionLabel>Создать контент</SectionLabel>
      <FormatTiles />

      <SectionLabel>Инструменты</SectionLabel>
      <ServiceRow
        to="/uniq"
        icon={<Layers size={20} />}
        title="Дубликатор видео"
        description="5 копий ролика для разных аккаунтов"
      />

      <DraftsBlock />

      <SectionLabel>Ещё</SectionLabel>
      <ServiceRow to="/history" icon={<History size={20} />} title="История" />

      <ReferralBlock />

      <footer className="flex items-center justify-between text-xs text-tg-hint mt-2">
        <span>Скоро: прогрев, лид-магнит</span>
        {user.isAdmin && (
          <Link
            to="/admin"
            className="inline-flex items-center gap-1 hover:text-tg-text transition-colors"
          >
            <Settings size={12} />
            Админка
          </Link>
        )}
      </footer>
    </div>
  );
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <h2 className="text-xs font-semibold uppercase tracking-wider text-tg-hint mb-2">{children}</h2>
  );
}

interface ServiceRowProps {
  to: string;
  icon: ReactNode;
  title: string;
  description?: string;
}

/** Нейтральная строка-ссылка: инструменты и служебные разделы. */
function ServiceRow({ to, icon, title, description }: ServiceRowProps) {
  return (
    <Link
      to={to}
      onClick={() => haptic('light')}
      className="flex items-center gap-3 w-full px-4 py-3 rounded-2xl bg-tg-section-bg text-tg-text mb-5 transition-[transform,opacity] duration-150 ease-smooth active:scale-[0.99] active:opacity-80"
    >
      <span className="flex-shrink-0 w-10 h-10 rounded-xl bg-tg-bg text-tg-text/80 flex items-center justify-center">
        {icon}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-semibold leading-tight">{title}</span>
        {description && (
          <span className="block text-xs text-tg-hint leading-snug mt-0.5">{description}</span>
        )}
      </span>
      <ArrowRight size={16} className="text-tg-hint flex-shrink-0" />
    </Link>
  );
}

// Подписи под названиями форматов на главной. Короче, чем description из БД,
// потому что тайл узкий. Неизвестный формат падает на description из API.
const TILE_COPY: Partial<Record<ContentFormatCode, string>> = {
  reels: 'Сценарий ролика за минуту',
  carousel: 'Готовые слайды для Instagram',
};

const FALLBACK_FORMATS: FormatDto[] = [
  { code: 'reels', name: 'Reels', description: TILE_COPY.reels ?? null, sort_order: 1 },
  { code: 'carousel', name: 'Карусели', description: TILE_COPY.carousel ?? null, sort_order: 3 },
];

function formatIcon(code: ContentFormatCode) {
  return code === 'carousel' ? <LayoutGrid size={18} /> : <Sparkles size={18} />;
}

/** Фиолетовые тайлы форматов, за которые платят кредитами. */
function FormatTiles() {
  const { data, isError } = useQuery({
    queryKey: ['formats'],
    queryFn: () => api.listFormats(),
    staleTime: 5 * 60_000,
    retry: 1,
  });
  const formats = isError || !data ? FALLBACK_FORMATS : data.formats;

  return (
    <div className="grid grid-cols-2 gap-3 mb-5">
      {formats.map((f) => (
        <Link
          key={f.code}
          to={`/ideas?format=${f.code}`}
          onClick={() => haptic('light')}
          className="flex flex-col justify-between gap-5 min-h-[124px] p-4 rounded-2xl bg-brand text-brand-on transition-[transform,opacity] duration-150 ease-smooth active:scale-[0.99] active:opacity-90"
        >
          <span className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
            {formatIcon(f.code)}
          </span>
          <span className="min-w-0">
            <span className="block text-base font-bold leading-tight">{f.name}</span>
            <span className="block text-xs opacity-80 leading-snug mt-0.5">
              {TILE_COPY[f.code] ?? f.description}
            </span>
          </span>
        </Link>
      ))}
    </div>
  );
}

const SHARE_TEXT =
  'Матрена пишет сценарии Reels, собирает карусели и делает копии роликов — попробуй!';

function ReferralBlock() {
  const { data, isError } = useQuery<ReferralInfoResponse>({
    queryKey: ['referral'],
    queryFn: () => api.getReferralInfo(),
    staleTime: 60_000,
    retry: false,
  });
  const [copied, setCopied] = useState(false);

  // 503 REFERRAL_NOT_CONFIGURED и любые другие ошибки — блок просто не показываем
  if (isError || !data) return null;

  function handleCopy() {
    if (!data) return;
    navigator.clipboard
      .writeText(data.link)
      .then(() => {
        haptic('success');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        // clipboard может быть недоступен в WebView — share остаётся
      });
  }

  function handleShare() {
    if (!data) return;
    haptic('light');
    WebApp.openTelegramLink(
      `https://t.me/share/url?url=${encodeURIComponent(data.link)}&text=${encodeURIComponent(SHARE_TEXT)}`,
    );
  }

  return (
    <Card variant="surface" padding="md" className="mb-4">
      <div className="flex items-start gap-3 mb-3">
        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-brand-muted text-brand flex items-center justify-center">
          <Gift size={20} />
        </div>
        <div className="min-w-0">
          <h2 className="text-base font-semibold mb-0.5">
            Приведи друга — получи +{data.rewardCredits} {pluralCredits(data.rewardCredits)}
          </h2>
          <p className="text-xs text-tg-hint leading-snug">
            Кредиты начислятся после первой покупки друга.
          </p>
        </div>
      </div>

      <p className="text-xs font-mono text-tg-hint truncate rounded-lg bg-tg-bg px-3 py-2 mb-2">
        {data.link}
      </p>

      <p className="text-xs text-tg-hint tabular-nums whitespace-nowrap mb-2">
        Приглашено: {data.invitedCount} · Купили: {data.rewardedCount}
      </p>

      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={handleCopy}
          className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-tg-bg text-sm font-medium transition-opacity active:opacity-70"
        >
          <Copy size={14} />
          {copied ? 'Готово' : 'Скопировать'}
        </button>
        <button
          type="button"
          onClick={handleShare}
          className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-brand text-brand-on text-sm font-medium transition-opacity active:opacity-70"
        >
          <Share2 size={14} />
          Поделиться
        </button>
      </div>

      {copied && (
        <Toast tone="success" placement="fixed">
          Ссылка скопирована
        </Toast>
      )}
    </Card>
  );
}
